import React, { useState, useEffect, useCallback } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'

const API_URL = process.env.REACT_APP_API_URL

const TrackerView = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [tracker, setTracker] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [visibleHistoryCount, setVisibleHistoryCount] = useState(5)

  const formatDate = (date) => {
    if (!date || date === "-") return "-";
    return new Date(date).toLocaleDateString();
  };

  const fetchTracker = useCallback(async () => {
    if (!id) {
      toast.error("Tracker ID is missing", { autoClose: 2000 })
      navigate('/trackerUpdate')
      return
    }

    setIsLoading(true)
    try {
      const payload = {
        action: "get-trackers-data",
        id: id
      }

      const response = await axios.post(API_URL, payload)

      let responseData = response.data
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData)
        } catch {}
      }

      if (responseData.success === true && responseData.data) {
        setTracker(responseData.data)
      } else {
        toast.error(responseData.message || "Failed to fetch tracker", { autoClose: 2000 })
        navigate('/trackerUpdate')
      }
    } catch (error) {
      console.error("Fetch tracker error:", error)
      toast.error("Failed to fetch tracker", { autoClose: 2000 })
      navigate('/trackerUpdate')
    } finally {
      setIsLoading(false)
    }
  }, [id, navigate])

  useEffect(() => {
    fetchTracker()
  }, [fetchTracker])

  const formatDateToIST = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  }

  const formatHistoryValue = (value) => {
    if (value === null || value === undefined || value === '') return '-'
    if (typeof value === 'object') {
      return (
        value.name ||
        value.clientname ||
        value.supplierName ||
        value.id ||
        value._id ||
        JSON.stringify(value)
      )
    }
    if (
      typeof value === 'string' &&
      /^\d{4}-\d{2}-\d{2}T/.test(value) &&
      !Number.isNaN(new Date(value).getTime())
    ) {
      return formatDateToIST(value)
    }
    return String(value)
  }

  const getHistoryActionStyle = (action) => {
    const normalized = (action || '').toLowerCase()
    if (normalized === 'created') {
      return { backgroundColor: '#E8F7EE', color: '#1E7E34' }
    }
    if (normalized === 'updated') {
      return { backgroundColor: '#EAF3FF', color: '#0B5ED7' }
    }
    return { backgroundColor: '#F1F3F5', color: '#495057' }
  }

  const formatFieldName = (field) => {
    if (!field) return '-'
    const fieldMap = {
      poNo: 'PO No',
      poDate: 'PO Date',
      itemDesc: 'Item Description',
      quantityOrdered: 'Quantity Ordered',
      quantityDispatched: 'Quantity Dispatched',
      poStatus: 'PO Status',
      poReceivedDate: 'PO Received Date',
      dispatchDate: 'Dispatch Date',
      deliveryDate: 'Delivery Date',
      grnStatus: 'GRN Status',
      paymentStatus: 'Payment Status',
      projectStatus: 'Project Status',
      comments: 'Comments',
      materialCode: 'Material Code',
      uom: 'UOM',
      customerName: 'Customer Name',
      spoNo: 'SPO No',
      spoDate: 'SPO Date',
      supplierName: 'Supplier',
      supplierId: 'Supplier Id',
      cpoBasicPrice: 'CPO Basic Price',
      spoBasicPrice: 'SPO Basic Price',
      total: 'Total',
      payment: 'Payment'
    }
    return fieldMap[field] || field
  }

  const getSupplierDisplay = (trackerData) => {
    if (!trackerData) return '-'
    if (trackerData.supplierName) return trackerData.supplierName
    const supplier = trackerData.supplierId
    if (supplier && typeof supplier === 'object') {
      return supplier.name || supplier.clientname || supplier.id || '-'
    }
    return '-'
  }

  const formatNumberValue = (value) => {
    if (value === null || value === undefined || value === '') return '-'
    const num = Number(value)
    if (!Number.isFinite(num)) return String(value)
    return Number.isInteger(num) ? String(num) : num.toFixed(2)
  }

  const historyList = Array.isArray(tracker?.history) ? tracker.history : []
  const sortedHistory = [...historyList].reverse()
  const visibleHistory = sortedHistory.slice(0, visibleHistoryCount)
  const hasMoreHistory = sortedHistory.length > visibleHistoryCount
  const latestHistory = historyList.length > 0 ? historyList[historyList.length - 1] : null

  useEffect(() => {
    setVisibleHistoryCount(5)
  }, [tracker?._id])

  if (isLoading) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6"><h1 className="m-0 txt_lft">View Customer Order</h1></div>
              <div className="col-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/trackerUpdate"><i className="fa-solid fa-arrow-left-long" /> Back</Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="frmwdth_cntr">
              <div className="card card-primary card-outline">
                <div className="card-body text-center py-5">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!tracker) {
    return (
      <div className="text-center py-5">
        <p>Tracker not found</p>
      </div>
    )
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6"><h1 className="m-0 txt_lft">View Customer Order</h1></div>
            <div className="col-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/trackerUpdate"><i className="fa-solid fa-arrow-left-long" /> Back</Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="frmwdth_cntr">
            <div className="card card-primary card-outline">
              <form className="form-horizontal">
                <div className="card-body">

            <div className="form-group row">
              <label className="col-md-4 col-form-label">PO No</label>
              <div className="col-md-8">
                <div className="txtdsply">{tracker.poNo || '-'}</div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">PO Date</label>
              <div className="col-md-8">
                <div className="txtdsply">{formatDate(tracker.poDate)}</div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">Material Code</label>
              <div className="col-md-8">
                <div className="txtdsply">{tracker.materialCode || '-'}</div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">UOM</label>
              <div className="col-md-8">
                <div className="txtdsply">{tracker.uom || '-'}</div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">Material Description</label>
              <div className="col-md-8">
                <div className="txtdsply">{tracker.itemDesc || '-'}</div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">Customer Name</label>
              <div className="col-md-8">
                <div className="txtdsply">{tracker.customerName || '-'}</div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">Quantity Ordered</label>
              <div className="col-md-8">
                <div className="txtdsply">{tracker.quantityOrdered ?? '-'}</div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">Quantity Dispatched</label>
              <div className="col-md-8">
                <div className="txtdsply">{tracker.quantityDispatched ?? '-'}</div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">PO Status</label>
              <div className="col-md-8">
                <div className="txtdsply">{tracker.poStatus || '-'}</div>
              </div>
            </div>

             <div className="form-group row">
              <label className="col-md-4 col-form-label">PO Received Date</label>
              <div className="col-md-8">
                <div className="txtdsply">{formatDate(tracker.poReceivedDate)}</div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">SPO No</label>
              <div className="col-md-8">
                <div className="txtdsply">{tracker.spoNo || '-'}</div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">SPO Date</label>
              <div className="col-md-8">
                <div className="txtdsply">{formatDate(tracker.spoDate)}</div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">Supplier</label>
              <div className="col-md-8">
                <div className="txtdsply">{getSupplierDisplay(tracker)}</div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">CPO Basic Price</label>
              <div className="col-md-8">
                <div className="txtdsply">{formatNumberValue(tracker.cpoBasicPrice)}</div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">SPO Basic Price</label>
              <div className="col-md-8">
                <div className="txtdsply">{formatNumberValue(tracker.spoBasicPrice)}</div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">Total Margin</label>
              <div className="col-md-8">
                <div className="txtdsply">{formatNumberValue(tracker.total)}</div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">Payment</label>
              <div className="col-md-8">
                <div className="txtdsply">{tracker.payment || '-'}</div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">Comments</label>
              <div className="col-md-8">
                <div className="txtdsply" style={{ whiteSpace: 'pre-wrap' }}>
                  {tracker.comments || '-'}
                </div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">Dispatch Date</label>
              <div className="col-md-8">
                <div className="txtdsply">{formatDate(tracker.dispatchDate)}</div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">Delivery Date</label>
              <div className="col-md-8">
                <div className="txtdsply">{formatDate(tracker.deliveryDate)}</div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">GRN Status</label>
              <div className="col-md-8">
                <div className="txtdsply">{tracker.grnStatus || '-'}</div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">Payment Status</label>
              <div className="col-md-8">
                <div className="txtdsply">{tracker.paymentStatus || '-'}</div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">Project Status</label>
              <div className="col-md-8">
                <div className="txtdsply">{tracker.projectStatus || '-'}</div>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-md-4 col-form-label">Created By</label>
              <div className="col-md-8">
                <div className="txtdsply">{tracker.createdBy?.name || '-'}</div>
              </div>
            </div>

                  <div className="form-group row">
                    <label className="col-md-4 col-form-label">Created At</label>
                    <div className="col-md-8">
                      <div className="txtdsply">{formatDateToIST(tracker.createdAt)}</div>
                    </div>
                  </div>

                  <div className="form-group row">
                    <label className="col-md-4 col-form-label">Updated By</label>
                    <div className="col-md-8">
                      <div className="txtdsply">{tracker.updatedBy?.name || '-'}</div>
                    </div>
                  </div>

                  <div className="form-group row">
                    <label className="col-md-4 col-form-label">Updated At</label>
                    <div className="col-md-8">
                      <div className="txtdsply">{formatDateToIST(tracker.updatedAt)}</div>
                    </div>
                  </div>

                  <hr />

                  <div className="mb-3">
                    <h5 className="mb-2">Change History</h5>
                    {latestHistory ? (
                      <div
                        className="p-3 mb-3"
                        style={{
                          borderRadius: '8px',
                          background: '#f8f9fa',
                          border: '1px solid #e9ecef'
                        }}
                      >
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#495057' }}>
                          Last Updated
                        </div>
                        <div style={{ fontSize: '13px' }}>
                          {latestHistory.changedByName || tracker.updatedBy?.name || '-'} on{' '}
                          {formatDateToIST(latestHistory.changedAt || tracker.updatedAt)}
                        </div>
                      </div>
                    ) : (
                      <div className="txtdsply">No history available</div>
                    )}

                    {historyList.length > 0 && (
                      <div
                        style={{
                          maxHeight: '520px',
                          overflowY: 'auto',
                          paddingRight: '4px'
                        }}
                      >
                        {visibleHistory.map((entry, historyIndex) => {
                          const changes = Array.isArray(entry.changes) ? entry.changes : []
                          const actionStyle = getHistoryActionStyle(entry.action)
                          return (
                            <div
                              key={`${entry.changedAt || historyIndex}-${entry.action || 'history'}`}
                              className="mb-3"
                              style={{
                                border: '1px solid #e9ecef',
                                borderRadius: '10px',
                                overflow: 'hidden',
                                backgroundColor: '#fff'
                              }}
                            >
                              <div
                                className="d-flex justify-content-between align-items-center flex-wrap"
                                style={{
                                  padding: '10px 12px',
                                  borderBottom: '1px solid #f1f3f5',
                                  backgroundColor: '#f8f9fa',
                                  gap: '8px'
                                }}
                              >
                                <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                                  <span
                                    style={{
                                      ...actionStyle,
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      textTransform: 'uppercase',
                                      padding: '3px 8px',
                                      borderRadius: '12px'
                                    }}
                                  >
                                    {entry.action || 'updated'}
                                  </span>
                                  <span style={{ fontSize: '12px', color: '#495057' }}>
                                    {entry.changedByName || 'Unknown user'}
                                  </span>
                                </div>
                                <span style={{ fontSize: '12px', color: '#6c757d' }}>
                                  {formatDateToIST(entry.changedAt)}
                                </span>
                              </div>

                              <div className="table-responsive">
                                <table className="table table-sm mb-0">
                                  <thead>
                                    <tr>
                                      <th style={{ fontSize: '11px', color: '#6c757d', width: '25%' }}>Field</th>
                                      <th style={{ fontSize: '11px', color: '#6c757d', width: '37.5%' }}>Old Value</th>
                                      <th style={{ fontSize: '11px', color: '#6c757d', width: '37.5%' }}>New Value</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {changes.length > 0 ? (
                                      changes.map((change, changeIndex) => (
                                        <tr key={`${entry.changedAt || historyIndex}-${change.field || changeIndex}`}>
                                          <td style={{ fontSize: '12px', fontWeight: 600 }}>
                                            {formatFieldName(change.field)}
                                          </td>
                                          <td style={{ fontSize: '12px', color: '#6c757d' }}>
                                            {formatHistoryValue(change.oldValue)}
                                          </td>
                                          <td style={{ fontSize: '12px', color: '#212529' }}>
                                            {formatHistoryValue(change.newValue)}
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan={3} style={{ fontSize: '12px', color: '#6c757d' }}>
                                          No field-level changes available.
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )
                        })}

                        <div className="d-flex justify-content-center align-items-center mt-3" style={{ gap: '10px' }}>
                          {hasMoreHistory && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => setVisibleHistoryCount((prev) => prev + 5)}
                            >
                              Show More History
                            </button>
                          )}
                          {visibleHistoryCount > 5 && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => setVisibleHistoryCount(5)}
                            >
                              Show Less
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TrackerView